import Dexie, { type EntityTable } from 'dexie'
import * as types from '../types'

// Define the database schema
interface BookEveryoneDB {
  people: EntityTable<types.Person, 'id'>
  calendars: EntityTable<types.Calendar, 'id'>
  groups: EntityTable<types.Group, 'id'>
  availability: EntityTable<types.Availability, 'calendarId'>
  settings: EntityTable<types.AppSettings & { id: string }, 'id'>
}

// Create the database instance
const db = new Dexie('BookEveryoneDB') as Dexie & BookEveryoneDB

// Define the schema
db.version(1).stores({
  people: 'id, firstName, lastName, email, [firstName+lastName]',
  calendars: 'id, personId, url, provider',
  groups: 'id, name',
  availability: 'calendarId, fetchedAt, expiresAt',
  settings: 'id'
})

// Hooks for automatic timestamps
db.people.hook('creating', (_primKey, obj) => {
  obj.createdAt = new Date()
  obj.updatedAt = new Date()
  obj.groups = obj.groups || []
  obj.calendars = obj.calendars || []
})

db.people.hook('updating', (modifications) => {
  return { ...modifications, updatedAt: new Date() }
})

// Helper functions
export const dbHelpers = {
  // Generate UUID
  generateId(): string {
    return crypto.randomUUID()
  },

  // Get default settings
  getDefaultSettings(): types.AppSettings & { id: string } {
    return {
      id: 'default',
      autoRefresh: false,
      refreshInterval: 30, // minutes
      syncOnLaunch: true,
      cacheDuration: 1, // hours
      theme: 'auto',
      weekStartsOn: 0, // Sunday
      timeFormat: '12'
    }
  },

  // Initialize settings if not exist
  async initializeSettings() {
    const settings = await db.settings.get('default')
    if (!settings) {
      await db.settings.add(this.getDefaultSettings())
    }
  },

  // Clear all data
  async clearAllData() {
    await db.transaction('rw', db.people, db.calendars, db.groups, db.availability, async () => {
      await db.people.clear()
      await db.calendars.clear()
      await db.groups.clear()
      await db.availability.clear()
    })
  },

  // Export data as JSON
  async exportData() {
    const people = await db.people.toArray()
    const calendars = await db.calendars.toArray()
    const groups = await db.groups.toArray()
    const availability = await db.availability.toArray()
    const settings = await db.settings.get('default')

    // Convert dates to strings for JSON serialization
    const exportData = {
      version: 1,
      exportedAt: new Date().toISOString(),
      data: {
        people: people.map(p => ({
          ...p,
          createdAt: p.createdAt?.toISOString ? p.createdAt.toISOString() : p.createdAt,
          updatedAt: p.updatedAt?.toISOString ? p.updatedAt.toISOString() : p.updatedAt,
          lastSyncedAt: p.lastSyncedAt?.toISOString ? p.lastSyncedAt.toISOString() : p.lastSyncedAt
        })),
        calendars: calendars.map(c => ({
          ...c,
          lastFetchedAt: c.lastFetchedAt?.toISOString ? c.lastFetchedAt.toISOString() : c.lastFetchedAt
        })),
        groups: groups.map(g => ({
          ...g,
          createdAt: g.createdAt?.toISOString ? g.createdAt.toISOString() : g.createdAt
        })),
        availability: availability.map(a => ({
          ...a,
          fetchedAt: a.fetchedAt?.toISOString ? a.fetchedAt.toISOString() : a.fetchedAt,
          expiresAt: a.expiresAt?.toISOString ? a.expiresAt.toISOString() : a.expiresAt
        })),
        settings
      }
    }

    return exportData
  },

  // Import data from JSON
  async importData(jsonData: any) {
    if (jsonData.version !== 1) {
      throw new Error('Unsupported data version')
    }

    await db.transaction('rw', [db.people, db.calendars, db.groups, db.availability, db.settings], async () => {
      // Clear existing data directly (not calling clearAllData to avoid nested transaction)
      await db.people.clear()
      await db.calendars.clear()
      await db.groups.clear()
      await db.availability.clear()
      await db.settings.clear()

      // Import new data with proper date conversion
      if (jsonData.data.people && jsonData.data.people.length > 0) {
        const peopleWithDates = jsonData.data.people.map((p: any) => ({
          ...p,
          createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
          updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
          lastSyncedAt: p.lastSyncedAt ? new Date(p.lastSyncedAt) : undefined
        }))
        await db.people.bulkAdd(peopleWithDates)
      }
      if (jsonData.data.calendars && jsonData.data.calendars.length > 0) {
        const calendarsWithDates = jsonData.data.calendars.map((c: any) => ({
          ...c,
          lastFetchedAt: c.lastFetchedAt ? new Date(c.lastFetchedAt) : undefined
        }))
        await db.calendars.bulkAdd(calendarsWithDates)
      }
      if (jsonData.data.groups && jsonData.data.groups.length > 0) {
        const groupsWithDates = jsonData.data.groups.map((g: any) => ({
          ...g,
          createdAt: g.createdAt ? new Date(g.createdAt) : new Date()
        }))
        await db.groups.bulkAdd(groupsWithDates)
      }
      if (jsonData.data.availability && jsonData.data.availability.length > 0) {
        for (const avail of jsonData.data.availability) {
          const availWithDates = {
            ...avail,
            fetchedAt: avail.fetchedAt ? new Date(avail.fetchedAt) : new Date(),
            expiresAt: avail.expiresAt ? new Date(avail.expiresAt) : new Date()
          }
          await db.availability.put(availWithDates)
        }
      }
      if (jsonData.data.settings) {
        await db.settings.put({ ...jsonData.data.settings, id: 'default' })
      }
    })
  }
}

// Initialize settings on first load
dbHelpers.initializeSettings()

export default db